import { useRef } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PriceDeskForm from "../components/PriceDeskForm";
import { usePageMeta } from "../hooks/usePageMeta";
import { 
  ArrowRight, 
  FileText, 
  MessageSquare, 
  ClipboardCheck, 
  Shield, 
  Clock, 
  Users,
  CheckCircle,
  FileSearch,
  Send,
  Table,
  Package
} from "lucide-react";
import { Link } from "react-router-dom";

const whatWeHandle = [
  {
    icon: Send,
    title: "Supplier Sourcing & RFQs",
    description: "Identifying appropriate suppliers, sending pricing requests with your specs, and following up until quotes land."
  },
  {
    icon: MessageSquare,
    title: "RFIs & Clarifications",
    description: "Handling supplier questions, getting clarification on specs, and resolving ambiguities before they become pricing gaps."
  },
  {
    icon: FileSearch,
    title: "Quote Follow-Up",
    description: "Chasing late quotes, prompting suppliers, and keeping the pricing pipeline moving so you're not waiting at deadline."
  },
  {
    icon: Table,
    title: "Quote Comparison",
    description: "Compiling received quotes into clear comparison formats so you can make decisions quickly."
  },
  {
    icon: Package,
    title: "Estimate-Ready Packs",
    description: "Delivering consolidated pricing in a format ready to drop into your estimate—no reformatting required."
  },
  {
    icon: ClipboardCheck,
    title: "Exclusion & Qualification Review",
    description: "Flagging supplier exclusions, qualifications, and conditions that could affect your pricing or risk."
  }
];

const howItWorks = [
  {
    step: "01",
    title: "Send Your Requirements",
    description: "Upload your plans, specs, and scope. Tell us which packages you need priced and when your tender closes."
  },
  {
    step: "02",
    title: "We Confirm the Plan",
    description: "Blackline reviews the scope, confirms capacity, and agrees the pricing plan and fee before starting."
  },
  {
    step: "03",
    title: "You Approve Contact",
    description: "You approve the supplier list and RFQ wording. Nothing goes out without your sign-off."
  },
  {
    step: "04",
    title: "We Handle the Legwork",
    description: "We send RFQs, handle follow-ups, answer RFIs, and chase quotes—keeping you informed at each step."
  },
  {
    step: "05",
    title: "Receive Estimate-Ready Pricing",
    description: "You get consolidated, compared, estimate-ready pricing before your deadline."
  }
];

const serviceOptions = [
  {
    id: "standalone",
    title: "Standalone Price Desk",
    description: "We handle the pricing legwork. You build the estimate.",
    features: [
      "Supplier sourcing & RFQs",
      "Quote follow-up & RFIs",
      "Quote comparison tables",
      "Estimate-ready pricing packs"
    ],
    cta: "Get Started",
    primary: false
  },
  {
    id: "with-estimate",
    title: "Price Desk + Estimate",
    description: "We source the pricing and build the estimate for you.",
    features: [
      "Full Price Desk service",
      "Complete estimate build",
      "Labour & materials",
      "Tender-ready output"
    ],
    cta: "Get Started",
    primary: true
  },
  {
    id: "with-review",
    title: "Price Desk + Tender Red Review",
    description: "We source pricing, then review the whole tender before submission.",
    features: [
      "Full Price Desk service",
      "Independent tender review",
      "Scope gap analysis",
      "Final submission check"
    ],
    cta: "Get Started",
    primary: false
  }
];

export default function PriceDeskPage() {
  usePageMeta("priceDesk");
  const formRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20 md:pt-24">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 bg-black text-white overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-15 grayscale"
            style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1920&q=80')",
            }}
          />
          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
              Blackline Price Desk
            </span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl uppercase mt-4 mb-6 max-w-4xl">
              We Handle the Pricing Legwork. You Keep Control of the Tender.
            </h1>
            <p className="font-body text-xl md:text-2xl text-zinc-300 max-w-3xl leading-relaxed mb-8">
              Supplier quotes, RFIs, follow-ups, comparisons—managed and compiled so you can focus on the estimate and submission.
            </p>
            <button
              onClick={scrollToForm}
              className="bg-white text-black h-14 px-8 uppercase font-bold tracking-widest border-2 border-white btn-brutal inline-flex items-center gap-3 hover:bg-zinc-200"
              data-testid="hero-cta"
            >
              Start a Price Desk Request
              <ArrowRight size={20} />
            </button>
          </div>
        </section>

        {/* Problem Statement */}
        <section className="py-16 md:py-24 border-t-4 border-black">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
              The Problem
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase mt-4 mb-6">
              Pricing Takes Time You Don't Have
            </h2>
            <div className="font-body text-lg text-zinc-700 space-y-4">
              <p>
                Chasing supplier quotes, sending RFQs, handling clarifications, following up on late responses, comparing apples with oranges—this is where estimating hours disappear.
              </p>
              <p>
                You know the trades, you know the project, but the pricing coordination pulls you away from actually building the estimate.
              </p>
              <p className="font-bold text-black">
                Blackline Price Desk handles the supplier and subcontractor pricing workload so you can stay focused on the numbers that matter.
              </p>
            </div>
          </div>
        </section>

        {/* What We Handle */}
        <section className="py-16 md:py-24 border-t-4 border-black bg-zinc-50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="mb-12">
              <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
                What We Handle
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase mt-4">
                The Pricing Legwork, Done Right
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {whatWeHandle.map((item, index) => (
                <div
                  key={index}
                  className="p-6 bg-white border-2 border-black"
                  data-testid={`feature-${index}`}
                >
                  <item.icon size={28} strokeWidth={1.5} className="mb-4" />
                  <h3 className="font-heading font-bold text-lg uppercase mb-2">
                    {item.title}
                  </h3>
                  <p className="font-body text-zinc-600 text-sm">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 md:py-24 border-t-4 border-black" id="how-it-works">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="mb-12">
              <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
                How It Works
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase mt-4">
                Simple Process, Clear Control
              </h2>
            </div>

            <div className="space-y-6">
              {howItWorks.map((step, index) => (
                <div
                  key={index}
                  className="flex gap-6 items-start p-6 border-2 border-black bg-white"
                  data-testid={`step-${index}`}
                >
                  <span className="font-mono text-3xl font-bold text-zinc-300 flex-shrink-0">
                    {step.step}
                  </span>
                  <div>
                    <h3 className="font-heading font-bold text-xl uppercase mb-2">
                      {step.title}
                    </h3>
                    <p className="font-body text-zinc-600">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Options */}
        <section className="py-16 md:py-24 border-t-4 border-black bg-zinc-50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="mb-12 text-center">
              <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
                Service Options
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase mt-4">
                Choose Your Level
              </h2>
              <p className="font-body text-zinc-600 mt-4 max-w-2xl mx-auto">
                Whether you just need the pricing managed or want us to build the whole estimate, there's an option that fits.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {serviceOptions.map((option) => (
                <div
                  key={option.id}
                  className={`p-6 md:p-8 flex flex-col border-2 border-black ${
                    option.primary ? "bg-black text-white" : "bg-white"
                  }`}
                  data-testid={`option-${option.id}`}
                >
                  <h3 className="font-heading font-bold text-xl uppercase mb-2">
                    {option.title}
                  </h3>
                  <p className={`font-body text-sm mb-6 ${option.primary ? "text-zinc-300" : "text-zinc-600"}`}>
                    {option.description}
                  </p>
                  
                  <ul className="space-y-3 flex-grow mb-6">
                    {option.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle size={16} className={`mt-0.5 flex-shrink-0 ${option.primary ? "text-white" : "text-black"}`} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    onClick={scrollToForm}
                    className={`h-12 px-6 uppercase font-bold text-sm tracking-widest border-2 flex items-center justify-center gap-2 ${
                      option.primary
                        ? "bg-white text-black border-white hover:bg-zinc-200"
                        : "bg-black text-white border-black hover:bg-zinc-800"
                    } transition-colors`}
                    data-testid={`option-cta-${option.id}`}
                  >
                    {option.cta}
                    <ArrowRight size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Control Section */}
        <section className="py-16 md:py-24 border-t-4 border-black bg-black text-white">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <Shield size={48} strokeWidth={1.5} className="mx-auto mb-6 text-zinc-400" />
            <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase mb-6">
              You Stay in Control
            </h2>
            <div className="font-body text-lg text-zinc-300 space-y-4 max-w-2xl mx-auto text-left">
              <p>
                Blackline manages the pricing process under your direction. You approve what goes out and what comes back in.
              </p>
              <ul className="space-y-3 mt-6">
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-white mt-0.5 flex-shrink-0" />
                  <span>You approve the supplier list before any contact</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-white mt-0.5 flex-shrink-0" />
                  <span>You see RFQ wording before it's sent</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-white mt-0.5 flex-shrink-0" />
                  <span>You receive all quotes—nothing hidden</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-white mt-0.5 flex-shrink-0" />
                  <span>You make the final pricing decisions</span>
                </li>
              </ul>
            </div>
            
            {/* Trust Statement */}
            <div className="mt-12 pt-8 border-t border-zinc-800">
              <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider">
                No orders placed. No quotes accepted. No external communication sent outside the agreed approval process.
              </p>
            </div>
          </div>
        </section>

        {/* Cross-links */}
        <section className="py-12 border-t-4 border-black bg-zinc-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <p className="font-body text-zinc-700 text-center mb-4">
              Already doing your own estimate? Blackline Price Desk can handle the supplier and subcontractor pricing workload for you.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/services"
                className="font-mono text-sm uppercase tracking-wider text-black hover:underline"
              >
                Estimating Services →
              </Link>
              <Link
                to="/tender-red-review"
                className="font-mono text-sm uppercase tracking-wider text-black hover:underline"
              >
                Tender Red Review →
              </Link>
              <Link
                to="/#contact"
                className="font-mono text-sm uppercase tracking-wider text-black hover:underline"
              >
                General Enquiry →
              </Link>
            </div>
          </div>
        </section>

        {/* Form Section */}
        <div ref={formRef}>
          <PriceDeskForm />
        </div>
      </main>

      <Footer />
    </div>
  );
}
